import { atom, selector, selectorFamily } from 'recoil';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { currentStructureSetState } from './structureSetAtoms';

export const defaultGlobalROIContourThickness = 1;

export type SymmetricMargin = {
    type: 'symmetric';
    value: number;
};

export type AsymmetricMargin = {
    type: 'asymmetric';
    left: number;
    right: number;
    top: number;
    bottom: number;
    anterior: number;
    posterior: number;
};

export type MarginValue = SymmetricMargin | AsymmetricMargin | null;

export const defaultMarginValue: MarginValue = null;

export const selectedROIState = atom<number>({
    key: 'selectedROIState',
    default: -1,
});

// Bulk-selected ROI numbers. Invariant: empty, or size >= 2. While non-empty,
// the active ROI (selectedROIState) is kept a member so the list highlight
// can't disagree with the selection. Never written by plain clicks; must be
// reset wherever selectedROIState is reset.
export const multiSelectedRoiNumbersState = atom<ReadonlySet<number>>({
    key: 'multiSelectedRoiNumbersState',
    default: new Set(),
});

// While bulk selection is engaged, contouring and ROI operations stay
// disabled: they act on the active ROI, which is misleading when the user
// is looking at a multi-selection.
export const bulkRoiSelectionActiveState = selector<boolean>({
    key: 'bulkRoiSelectionActiveState',
    get: ({ get }) => get(multiSelectedRoiNumbersState).size >= 2,
});

// True iff the selected ROI's visibility is ephemeral (auto-shown by
// selection, or solo after Hide-all). Step-through reads this to decide
// whether to hide the previous selection on move-away.
export const selectionAutoShownState = atom<boolean>({
    key: 'selectionAutoShownState',
    default: false,
});

export const openedStructureDeletionConfirmationDialogState = atom<
    number | null
>({
    key: 'openedStructureDeletionConfirmationDialogState',
    default: null,
});

/**
 * When set to a ROI number, triggers inline rename mode for that ROI
 * in the StructureField component. Reset to null after consumption.
 */
export const renamingRoiState = atom<number | null>({
    key: 'renamingRoiState',
    default: null,
});

export const openedStructureSetApprovalConfirmationDialogState = atom<{
    sopUid: string;
    newApprovalStatus: DICOMApprovalStatus;
} | null>({
    key: 'openedStructureSetApprovalConfirmationDialogState',
    default: null,
});

export const globalROIContourThicknessState = atom<number>({
    key: 'globalROIContourThicknessState',
    default: defaultGlobalROIContourThickness,
});

export const roisVisibilityState = selectorFamily({
    key: 'roisVisibilityState',
    get:
        (roiNumbers: number[]) =>
        ({ get }) => {
            const visibilityMap = new Map<number, boolean>();
            const currentStructureSet = get(currentStructureSetState);
            roiNumbers.forEach((roiNumber) => {
                const roiReadModel = currentStructureSet?.roiArray.find(
                    (roi) => roi.roiNumber === roiNumber
                );
                const isVisible = roiReadModel?.visible ?? false;
                visibilityMap.set(roiNumber, isVisible);
            });

            return visibilityMap;
        },
});

export const roiColorsState = selectorFamily({
    key: 'roiColorsState',
    get:
        (roiNumbers: number[]) =>
        ({ get }) => {
            const colorsMap = new Map<number, string>();
            const currentStructureSet = get(currentStructureSetState);
            roiNumbers.forEach((roiNumber) => {
                const roiReadModel = currentStructureSet?.roiArray.find(
                    (roi) => roi.roiNumber === roiNumber
                );
                if (!roiReadModel) {
                    return;
                }
                const { R, G, B } = roiReadModel.color;
                const rgbColor = `rgb(${R}, ${G}, ${B})`;
                colorsMap.set(roiNumber, rgbColor);
            });

            return colorsMap;
        },
});

/** True while any SDF post-processing operation is in flight. */
export const sdfOperationInFlightState = atom<boolean>({
    key: 'sdfOperationInFlightState',
    default: false,
});

export const roisForDvhState = selector({
    key: 'roisForDvhState',
    get: ({ get }) => {
        const currentStructureSet = get(currentStructureSetState);
        if (!currentStructureSet) {
            return [];
        }
        return currentStructureSet.roiArray.filter((r) => r.visible);
    },
});

export const selectedRoiIsPointTypeState = selector<boolean>({
    key: 'selectedRoiIsPointTypeState',
    get: ({ get }) => {
        const selectedRoiNumber = get(selectedROIState);
        if (selectedRoiNumber === -1) {
            return false;
        }
        const structureSet = get(currentStructureSetState);
        const roi = structureSet?.roiArray.find(
            (r) => r.roiNumber === selectedRoiNumber
        );
        return roi?.isPointRoi ?? false;
    },
});
